import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Barcode,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Search,
  Filter,
  Sparkles,
  Maximize2,
  X,
  FileSpreadsheet,
  Check,
  CheckCheck,
  RefreshCw,
  Eye,
  EyeOff,
  Store,
  Layers,
  Send,
  Receipt,
  ChevronLeft,
  ChevronRight,
  Boxes,
} from 'lucide-react';
import {
  fetchFinalEanMatch,
  fetchFinalEanMatchKpis,
  fetchFinalEanMatchBrands,
  applyTopfaceEan,
  fetchTopfaceSuggestions,
  confirmFinalEanMatch,
  unconfirmFinalEanMatch,
  confirmBulkFinalEanMatch,
  rejectFinalEanMatch,
  applyWholesalerPrice,
  fetchJson,
  releaseFinalEanMatchClaims,
  type FinalEanMatchRow,
} from '../api.js';
import { Button } from '../ui/index.js';
import { cn } from '../ui/cn.js';
import { SubBrandPills } from '../components/SubBrandPills.js';

function formatTnd(millimes: number | null | undefined): string {
  if (millimes === null || millimes === undefined || !Number.isFinite(millimes)) return '—';
  return (millimes / 1000).toFixed(3) + ' TND';
}

export function FinalEanMatchPage(): React.ReactElement {
  const queryClient = useQueryClient();

  // Filters & State
  const [page, setPage] = useState<number>(0);
  const [limit] = useState<number>(50);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [excludedSubBrands, setExcludedSubBrands] = useState<string[]>([]);
  const [matchFilter, setMatchFilter] = useState<string>('all');
  const [layer2Filter, setLayer2Filter] = useState<'all' | 'pending' | 'confirmed'>('pending');
  // exclusive per-operator batch (like the Workbench queue) vs global browse
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Zoom modal state
  const [zoomItem, setZoomItem] = useState<FinalEanMatchRow | null>(null);

  // Topface suggestions modal state
  const [topfaceModalProduct, setTopfaceModalProduct] = useState<FinalEanMatchRow | null>(null);
  const [topfaceCustomSearch, setTopfaceCustomSearch] = useState<string>('');

  // Lock body scroll and handle Escape key for open modals
  useEffect(() => {
    if (!zoomItem && !topfaceModalProduct) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (zoomItem) setZoomItem(null);
        if (topfaceModalProduct) setTopfaceModalProduct(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [zoomItem, topfaceModalProduct]);

  // Handle search typing with simple manual debounce on enter or change
  const handleSearchChange = (val: string) => {
    setSearch(val);
    setPage(0);
    setDebouncedSearch(val);
  };

  // Queries
  const kpisQuery = useQuery({
    queryKey: ['final-ean-match-kpis'],
    queryFn: fetchFinalEanMatchKpis,
    refetchInterval: 30_000,
  });

  const brandsQuery = useQuery({
    queryKey: ['final-ean-match-brands'],
    queryFn: fetchFinalEanMatchBrands,
    staleTime: 60_000,
  });

  const listQuery = useQuery({
    queryKey: ['final-ean-match', page, limit, debouncedSearch, selectedBrand, excludedSubBrands, matchFilter, layer2Filter, scope],
    queryFn: () =>
      fetchFinalEanMatch({
        page,
        limit,
        q: debouncedSearch,
        brand: selectedBrand,
        excludedSubBrands: excludedSubBrands.length > 0 ? excludedSubBrands.join(',') : undefined,
        matchFilter,
        scope,
        statusFilter:
          layer2Filter === 'pending'
            ? 'pending_layer2'
            : layer2Filter === 'confirmed'
              ? 'confirmed_layer2'
              : 'all',
      }),
  });

  // Release my reserved batch (like the Workbench « Libérer mon lot »)
  const releaseClaims = useMutation({
    mutationFn: () => releaseFinalEanMatchClaims(),
    onSuccess: (res) => {
      setToastMessage(`Votre lot (${res.released} produit${res.released > 1 ? 's' : ''}) a été libéré.`);
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match'] });
    },
    onError: (err) => setToastMessage(`Erreur : ${String(err.message)}`),
  });

  // Topface manual suggestions query
  const suggestionsQuery = useQuery({
    queryKey: ['topface-suggestions', topfaceModalProduct?.id, topfaceCustomSearch],
    queryFn: () =>
      fetchTopfaceSuggestions({
        productId: topfaceModalProduct?.id,
        q: topfaceCustomSearch || undefined,
      }),
    enabled: topfaceModalProduct !== null,
  });

  // Mutation: Apply Topface EAN
  const applyEanMutation = useMutation({
    mutationFn: (input: { productId: number; gtin: string; offerId?: number }) => applyTopfaceEan(input),
    onSuccess: (res) => {
      setToastMessage(`Code-barres ${res.gtin} validé et appliqué à ${res.productName}.`);
      setTopfaceModalProduct(null);
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match-kpis'] });
    },
    onError: (err) => {
      setToastMessage(`Erreur lors de l'application de l'EAN : ${String(err.message)}`);
    },
  });

  // Action feedback message per product card
  const [actionFeedback, setActionFeedback] = useState<{
    productId: number;
    type: 'confirm' | 'reject';
    message: string;
    targetGtin?: string;
  } | null>(null);

  // Conflict modal state for shared barcode / assortment variations
  const [conflictModal, setConflictModal] = useState<{
    productId: number;
    productName: string;
    sku?: string | null | undefined;
    gtin: string;
    conflictingProduct?: { id: number; name: string; sku?: string | null } | undefined;
    detail?: string | undefined;
  } | null>(null);

  // Mutation: Adopt wholesaler price directly from card
  const adoptWholesalerPriceMutation = useMutation({
    mutationFn: (input: { productId: number; offerId?: number; priceHt?: number; sourceKey?: string }) =>
      applyWholesalerPrice(input.productId, input),
    onSuccess: (res) => {
      setToastMessage(`✓ Prix d'achat grossiste ${(res.priceHt / 1000).toFixed(3)} TND HT adopté avec succès !`);
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match-kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] });
    },
    onError: (err) => {
      setToastMessage(`Erreur adoption prix grossiste : ${String(err)}`);
    },
  });

  // Mutation: Confirm EAN (Approved in GTIN Workbench)
  const confirmMutation = useMutation({
    mutationFn: (input: {
      productId: number;
      gtin?: string;
      allowSharedGtin?: boolean;
      generateInternal?: boolean;
    }) => confirmFinalEanMatch(input),
    onSuccess: (res) => {
      setToastMessage(
        `✓ Code GTIN ${res.gtin} validé en Couche 2 (100% vérifié) ! Produit masqué de la file et transféré vers S19 Prix Grossistes.`,
      );
      setActionFeedback({
        productId: res.productId,
        type: 'confirm',
        message: `Code ${res.gtin} validé et approuvé en Couche 2 !`,
        targetGtin: res.gtin,
      });
      setConflictModal(null);
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match-kpis'] });
      if (zoomItem && zoomItem.id === res.productId) {
        setZoomItem(null);
      }
    },
    onError: (err: unknown, variables) => {
      const errObj = (typeof err === 'object' && err !== null ? err : {}) as {
        error?: string;
        gtin?: string;
        conflictingProduct?: { id: number; name: string; sku?: string | null };
        detail?: string;
        message?: string;
      };
      if (errObj.error === 'gtin_already_assigned') {
        const productInList = listQuery.data?.items?.find((r) => r.id === variables.productId);
        setConflictModal({
          productId: variables.productId,
          productName: productInList?.name ?? `Produit #${variables.productId}`,
          sku: productInList?.sku ?? null,
          gtin: errObj.gtin ?? variables.gtin ?? '',
          conflictingProduct: errObj.conflictingProduct,
          detail: typeof errObj.detail === 'string' ? errObj.detail : undefined,
        });
        return;
      }
      setToastMessage(`Erreur lors de la validation : ${String(errObj.message || err)}`);
    },
  });

  // Mutation: Unconfirm EAN (Cancel Layer 2 validation)
  const unconfirmMutation = useMutation({
    mutationFn: (productId: number) => unconfirmFinalEanMatch(productId),
    onSuccess: (res) => {
      setToastMessage(`Validation Couche 2 annulée pour le produit #${res.productId}. Il est de nouveau en attente de vérification.`);
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match-kpis'] });
      if (zoomItem && zoomItem.id === res.productId) {
        setZoomItem(null);
      }
    },
    onError: (err) => {
      setToastMessage(`Erreur lors de l'annulation : ${String(err)}`);
    },
  });

  // Mutation: Reject product (sent back to review in GTIN Workbench)
  const rejectMutation = useMutation({
    mutationFn: (input: { productId: number; note?: string }) => rejectFinalEanMatch(input),
    onSuccess: (res) => {
      setToastMessage(`⚠️ Produit #${res.productId} rejeté — renvoyé dans la file de révision Workbench pour nouveau rapprochement.`);
      setActionFeedback({
        productId: res.productId,
        type: 'reject',
        message: `Produit rejeté — renvoyé en file de révision Workbench.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match-kpis'] });
      if (zoomItem && zoomItem.id === res.productId) {
        setZoomItem(null);
      }
    },
    onError: (err) => {
      setToastMessage(`Erreur lors du rejet : ${String(err.message)}`);
    },
  });

  // Mutation: Bulk Confirm / Validate all EANs on current page
  const confirmBulkMutation = useMutation({
    mutationFn: (itemsToConfirm: Array<{ productId: number; gtin: string }>) =>
      confirmBulkFinalEanMatch({ items: itemsToConfirm }),
    onSuccess: (res) => {
      setToastMessage(
        `✓ ${res.confirmedCount} produit(s) confirmés en Couche 2 (double vérification 100%) ! Masqués de cette file et actifs sur l'écran S19 (Prix Grossistes).${res.skippedCount > 0 ? ` (${res.skippedCount} sans code restent en attente)` : ''}`,
      );
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match-kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
      void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] });
    },
    onError: (err) => {
      setToastMessage(`Erreur lors de la validation par lot : ${String(err.message)}`);
    },
  });

  // Mutation: Hide product (Masquer de l'application et de WooCommerce)
  const hideMutation = useMutation({
    mutationFn: (productId: number) =>
      fetchJson<{ ok: boolean; hiddenCount: number }>('/api/v1/shipping/hide-bulk', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ productIds: [productId] }),
      }),
    onSuccess: () => {
      setToastMessage(`Produit masqué sur l'application et envoyé pour masquage sur WooCommerce.`);
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match-kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
    },
    onError: (err) => {
      setToastMessage(`Erreur lors du masquage : ${String(err.message)}`);
    },
  });

  const kpis = kpisQuery.data;
  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = listQuery.data?.totalPages ?? 0;
  const claim = listQuery.data?.claim ?? null;

  // Candidate EAN extraction for each product
  const getCandidateGtin = (row: FinalEanMatchRow): string | null => {
    return row.gtin || row.barcodeRaw || row.eanRaw || row.pharmashop?.gtin || row.parashop?.gtin || row.topfaceMatch?.barcode || null;
  };

  // Products needing Layer 2 confirmation on current page
  const unconfirmedItems: Array<{ productId: number; gtin: string }> = items
    .filter((row) => !row.finalEanConfirmed)
    .map((row) => ({
      productId: row.id,
      gtin: getCandidateGtin(row) ?? '',
    }))
    .filter((it): it is { productId: number; gtin: string } => typeof it.gtin === 'string' && it.gtin.trim() !== '');

  // Eligible products with an EAN on current page (prioritizes unconfirmed items)
  const eligibleItems: Array<{ productId: number; gtin: string }> = unconfirmedItems.length > 0
    ? unconfirmedItems
    : items
        .map((row) => ({
          productId: row.id,
          gtin: getCandidateGtin(row) ?? '',
        }))
        .filter((it): it is { productId: number; gtin: string } => typeof it.gtin === 'string' && it.gtin.trim() !== '');

  const handleApproveAllPage = () => {
    if (eligibleItems.length === 0) {
      alert("Aucun produit sur cette page ne possède de code EAN à valider.");
      return;
    }
    const confirmMsg =
      unconfirmedItems.length > 0
        ? `Valider la Couche 2 (double vérification visuelle) pour les ${unconfirmedItems.length} produit(s) en attente de cette page ?\n\nCes produits deviendront immédiatement actifs sur l'écran S19 (Rapprochement Prix Grossistes).`
        : `Revalider la Couche 2 pour les ${eligibleItems.length} produits de cette page ?`;

    if (window.confirm(confirmMsg)) {
      confirmBulkMutation.mutate(eligibleItems);
    }
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Toast notification banner */}
      {toastMessage && (
        <div className="sticky top-2 z-40 mx-auto max-w-2xl rounded-xl bg-herb-800 px-4 py-3 text-white shadow-xl flex items-center justify-between border border-herb-600/30">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
            <span className="text-sm font-medium">{toastMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setToastMessage(null)}
            className="rounded p-1 hover:bg-herb-700 text-white/80 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-herb-100 border border-herb-600/20 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-herb-800">
              S23 · Rapprochement EAN Final
            </span>
            <span className="rounded-full bg-sky-100 border border-sky-600/20 px-2 py-0.5 text-[11px] font-bold text-sky-800">
              Pharmashop & Parashop uniquement
            </span>
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink font-display">
            Final EAN Match
          </h1>
          <p className="text-sm text-ink-soft">
            Contrôle visuel grand format & correspondance EAN de tous les produits approuvés contre Pharma-Shop et Parashop.
            Rapprochement automatisé des produits Topface par leur tarif fournisseur officiel.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/gtin-pricing-vat?filter=approved&missingPrice=true"
            className="inline-flex items-center gap-1.5 rounded-xl border border-herb-600/30 bg-herb-50 hover:bg-herb-100/90 px-3.5 py-1.5 text-xs font-bold text-herb-800 shadow-2xs transition-colors"
            title="Aller sur l'écran S19 pour rapprocher les prix d'achat grossistes de ces produits"
          >
            <Receipt className="h-4 w-4 text-herb-700" />
            Prix d'Achat Grossistes (S19) →
          </Link>
          <Button
            variant="soft"
            size="sm"
            onClick={() => {
              void listQuery.refetch();
              void kpisQuery.refetch();
            }}
            disabled={listQuery.isFetching}
          >
            <RefreshCw className={cn('h-4 w-4', listQuery.isFetching && 'animate-spin')} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Per-operator exclusive batch — same mutual exclusion as the Workbench queue */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card/80 px-4 py-3 text-xs shadow-xs">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <span
            className={cn(
              'flex h-2.5 w-2.5 shrink-0 rounded-full ring-2',
              scope === 'mine'
                ? 'animate-pulse bg-emerald-500 ring-emerald-200'
                : 'bg-slate-400 ring-slate-200',
            )}
          />
          {scope === 'mine' ? (
            <div className="min-w-0">
              <span className="font-semibold text-ink">Mode Multi-Opérateur Actif :</span>{' '}
              <span className="text-ink-soft">
                {claim !== null && claim.myCount > 0
                  ? `Lot exclusif de ${claim.myCount} produit(s) réservé pour vous — ${claim.totalPending} en attente au total (aucun autre opérateur ne voit ces produits).`
                  : 'Chargez cette page pour réserver un lot exclusif (30 minutes).'}
              </span>
              {claim !== null && claim.reviewers.length > 0 && (
                <span className="ml-2 inline-flex flex-wrap items-center gap-1.5 align-middle">
                  {claim.reviewers.map((r) => (
                    <span
                      key={r.userId}
                      className="inline-flex items-center rounded-md border border-border bg-muted px-2 py-0.5 font-medium text-ink-muted"
                    >
                      👤 {r.name} ({r.count})
                    </span>
                  ))}
                </span>
              )}
            </div>
          ) : (
            <div className="min-w-0">
              <span className="font-semibold text-ink">Vue globale :</span>{' '}
              <span className="text-ink-soft">
                tous les produits en attente — rien n&apos;est réservé, un autre opérateur peut
                travailler le même produit en même temps.
              </span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {scope === 'mine' && claim !== null && claim.myCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              disabled={releaseClaims.isPending}
              onClick={() => releaseClaims.mutate()}
              title="Libérer mes produits réservés pour qu'un autre opérateur puisse les traiter"
            >
              {releaseClaims.isPending ? 'Libération…' : '🔓 Libérer mon lot'}
            </Button>
          )}
          <div className="flex overflow-hidden rounded-lg border border-border">
            <button
              type="button"
              onClick={() => {
                setScope('mine');
                setPage(0);
              }}
              className={cn(
                'px-3 py-1.5 font-semibold transition-colors',
                scope === 'mine' ? 'bg-herb-600 text-white' : 'bg-card text-ink-soft hover:bg-muted',
              )}
            >
              Mon lot
            </button>
            <button
              type="button"
              onClick={() => {
                setScope('all');
                setPage(0);
              }}
              className={cn(
                'px-3 py-1.5 font-semibold transition-colors',
                scope === 'all' ? 'bg-herb-600 text-white' : 'bg-card text-ink-soft hover:bg-muted',
              )}
            >
              Tous
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards Banner */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
        <div className="rounded-xl border border-border bg-card p-3.5 shadow-2xs">
          <div className="flex items-center justify-between text-ink-ghost">
            <span className="text-xs font-semibold uppercase tracking-wider">Couche 1 (Fil d'attente)</span>
            <CheckCircle2 className="h-4 w-4 text-slate-500" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-ink font-mono">
              {kpis?.totalApproved?.toLocaleString('fr-FR') ?? '—'}
            </span>
            <span className="text-xs text-ink-ghost">approuvés</span>
          </div>
          <p className="mt-1 text-[11px] text-ink-soft">1ère validation fil d'attente</p>
        </div>

        <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-3.5 shadow-2xs">
          <div className="flex items-center justify-between text-amber-800">
            <span className="text-xs font-bold uppercase tracking-wider">À valider (Couche 2)</span>
            <Sparkles className="h-4 w-4 text-amber-600" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-amber-900 font-mono">
              {kpis?.layer2PendingCount?.toLocaleString('fr-FR') ?? '—'}
            </span>
            <span className="text-xs font-semibold text-amber-800">en attente</span>
          </div>
          <p className="mt-1 text-[11px] text-amber-900">Contrôle visuel grand format</p>
        </div>

        <div className="rounded-xl border border-emerald-300 bg-emerald-50/70 p-3.5 shadow-2xs">
          <div className="flex items-center justify-between text-emerald-900">
            <span className="text-xs font-bold uppercase tracking-wider">Validés 100% (Couche 2)</span>
            <CheckCheck className="h-4 w-4 text-emerald-700" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-emerald-900 font-mono">
              {kpis?.layer2ConfirmedCount?.toLocaleString('fr-FR') ?? '—'}
            </span>
            <span className="text-xs font-semibold text-emerald-800">actifs S19</span>
          </div>
          <p className="mt-1 text-[11px] text-emerald-900">Double validation garantie 100%</p>
        </div>

        <div className="rounded-xl border border-border bg-card p-3.5 shadow-2xs">
          <div className="flex items-center justify-between text-ink-ghost">
            <span className="text-xs font-semibold uppercase tracking-wider">Matché Pharmashop</span>
            <Store className="h-4 w-4 text-sky-600" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-sky-700 font-mono">
              {kpis?.matchedPharmashop?.toLocaleString('fr-FR') ?? '—'}
            </span>
            {kpis && kpis.totalApproved > 0 && (
              <span className="text-xs font-semibold text-sky-600">
                {Math.round((kpis.matchedPharmashop / kpis.totalApproved) * 100)}%
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-ink-soft">Présents sur pharma-shop.tn</p>
        </div>

        <div className="rounded-xl border border-border bg-card p-3.5 shadow-2xs">
          <div className="flex items-center justify-between text-ink-ghost">
            <span className="text-xs font-semibold uppercase tracking-wider">Matché Parashop</span>
            <Store className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-emerald-700 font-mono">
              {kpis?.matchedParashop?.toLocaleString('fr-FR') ?? '—'}
            </span>
            {kpis && kpis.totalApproved > 0 && (
              <span className="text-xs font-semibold text-emerald-600">
                {Math.round((kpis.matchedParashop / kpis.totalApproved) * 100)}%
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-ink-soft">Présents sur parashop.tn</p>
        </div>

        <div className="rounded-xl border border-border bg-card p-3.5 shadow-2xs">
          <div className="flex items-center justify-between text-ink-ghost">
            <span className="text-xs font-bold uppercase tracking-wider">File À corriger (Woo)</span>
            <Send className="h-4 w-4 text-emerald-700" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-ink font-mono">
              {kpis?.correctionsCount?.toLocaleString('fr-FR') ?? '—'}
            </span>
            <span className="text-xs font-semibold text-ink-soft">en attente</span>
          </div>
          <p className="mt-1 text-[11px] text-ink-soft">
            EAN validés à pousser sur WooCommerce
          </p>
        </div>

        <div
          onClick={() => {
            setMatchFilter('divergent');
            setPage(0);
          }}
          className={cn(
            'rounded-xl border p-3.5 shadow-2xs cursor-pointer transition-all',
            matchFilter === 'divergent'
              ? 'border-rose-400 bg-rose-50 ring-2 ring-rose-500/30'
              : 'border-rose-200 bg-rose-50/50 hover:bg-rose-50',
          )}
        >
          <div className="flex items-center justify-between text-rose-800">
            <span className="text-xs font-bold uppercase tracking-wider">Divergences EAN</span>
            <AlertCircle className="h-4 w-4 text-rose-600" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-rose-900 font-mono">
              {kpis?.divergentCount?.toLocaleString('fr-FR') ?? '—'}
            </span>
            <span className="text-xs font-semibold text-rose-700">anomalies</span>
          </div>
          <p className="mt-1 text-[11px] text-rose-800">
            Écart marque / libellé concurrent
          </p>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="rounded-2xl border border-border bg-card p-4 shadow-xs space-y-3">
        {/* Layer 2 Mode Tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border/60 pb-3">
          <span className="text-xs font-bold text-ink-soft uppercase tracking-wider mr-1">
            Vérification :
          </span>

          <button
            type="button"
            onClick={() => { setLayer2Filter('pending'); setPage(0); }}
            className={cn(
              'rounded-xl px-3.5 py-1.5 text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer',
              layer2Filter === 'pending'
                ? 'bg-amber-600 text-white shadow-2xs'
                : 'bg-amber-50 text-amber-900 border border-amber-300 hover:bg-amber-100',
            )}
          >
            <Sparkles className="h-3.5 w-3.5" />
            ⏳ À valider (Couche 2)
            <span className="rounded-full bg-white/20 px-1.5 py-0.2 font-mono text-[11px]">
              {kpis?.layer2PendingCount ?? '—'}
            </span>
          </button>

          <button
            type="button"
            onClick={() => { setLayer2Filter('confirmed'); setPage(0); }}
            className={cn(
              'rounded-xl px-3.5 py-1.5 text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer',
              layer2Filter === 'confirmed'
                ? 'bg-emerald-700 text-white shadow-2xs'
                : 'bg-emerald-50 text-emerald-900 border border-emerald-300 hover:bg-emerald-100',
            )}
          >
            <CheckCheck className="h-3.5 w-3.5" />
            ✓ Validés 100% (Couche 2 - S19)
            <span className="rounded-full bg-white/20 px-1.5 py-0.2 font-mono text-[11px]">
              {kpis?.layer2ConfirmedCount ?? '—'}
            </span>
          </button>

          <button
            type="button"
            onClick={() => { setLayer2Filter('all'); setPage(0); }}
            className={cn(
              'rounded-xl px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer',
              layer2Filter === 'all'
                ? 'bg-slate-800 text-white shadow-2xs'
                : 'bg-cream/60 text-ink-soft hover:bg-cream hover:text-ink',
            )}
          >
            Tous les approuvés ({kpis?.totalApproved ?? '—'})
          </button>
        </div>

        {/* Competitor / Brand Sub-Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold text-ink-soft uppercase tracking-wider mr-1 flex items-center gap-1">
            <Filter className="h-3.5 w-3.5" /> Source :
          </span>

          <button
            type="button"
            onClick={() => { setMatchFilter('all'); setPage(0); }}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer',
              matchFilter === 'all'
                ? 'bg-herb-700 text-white shadow-2xs'
                : 'bg-cream/60 text-ink-soft hover:bg-cream hover:text-ink',
            )}
          >
            Toutes sources
          </button>

          <button
            type="button"
            onClick={() => { setMatchFilter('both'); setPage(0); }}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer',
              matchFilter === 'both'
                ? 'bg-herb-700 text-white shadow-2xs'
                : 'bg-cream/60 text-ink-soft hover:bg-cream hover:text-ink',
            )}
          >
            Pharma-Shop & Parashop ({kpis?.matchedBoth ?? '—'})
          </button>

          <button
            type="button"
            onClick={() => { setMatchFilter('pharma'); setPage(0); }}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer',
              matchFilter === 'pharma'
                ? 'bg-sky-700 text-white shadow-2xs'
                : 'bg-sky-50 text-sky-800 hover:bg-sky-100',
            )}
          >
            Pharmashop ({kpis?.matchedPharmashop ?? '—'})
          </button>

          <button
            type="button"
            onClick={() => { setMatchFilter('para'); setPage(0); }}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer',
              matchFilter === 'para'
                ? 'bg-emerald-700 text-white shadow-2xs'
                : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
            )}
          >
            Parashop ({kpis?.matchedParashop ?? '—'})
          </button>

          <button
            type="button"
            onClick={() => {
              setMatchFilter('topface_all');
              setSelectedBrand('Topface');
              setPage(0);
            }}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-bold transition-all border border-amber-300 cursor-pointer',
              matchFilter === 'topface_all'
                ? 'bg-amber-600 text-white shadow-2xs'
                : 'bg-amber-50 text-amber-900 hover:bg-amber-100',
            )}
          >
            ⭐ Topface (Tous: {kpis?.topfaceTotal ?? '—'})
          </button>

          <button
            type="button"
            onClick={() => {
              setMatchFilter('topface_unassigned');
              setSelectedBrand('Topface');
              setPage(0);
            }}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-bold transition-all border border-red-300 cursor-pointer',
              matchFilter === 'topface_unassigned'
                ? 'bg-rose-700 text-white shadow-2xs'
                : 'bg-rose-50 text-rose-800 hover:bg-rose-100',
            )}
          >
            ⚠️ Topface sans EAN ({kpis?.topfaceMissingEan ?? '—'})
          </button>

          <button
            type="button"
            onClick={() => {
              setMatchFilter('divergent');
              setPage(0);
            }}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-bold transition-all border cursor-pointer flex items-center gap-1.5',
              matchFilter === 'divergent'
                ? 'bg-rose-700 text-white shadow-2xs border-rose-800'
                : 'bg-rose-50 text-rose-800 border-rose-300 hover:bg-rose-100',
            )}
          >
            <AlertCircle className="h-3.5 w-3.5" />
            ⚠️ Divergences EAN ({kpis?.divergentCount ?? '—'})
          </button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {/* Search box */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-ghost" />
            <input
              type="text"
              placeholder="Rechercher par nom de produit, SKU ou code-barres EAN..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full rounded-xl border border-border bg-card pl-9 pr-4 py-2 text-xs sm:text-sm text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:outline-none focus:ring-1 focus:ring-herb-600"
            />
            {search && (
              <button
                type="button"
                onClick={() => handleSearchChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-ghost hover:text-ink"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Brand select */}
          <div className="w-full sm:w-64">
            <select
              value={selectedBrand}
              onChange={(e) => {
                setSelectedBrand(e.target.value);
                setExcludedSubBrands([]);
                setPage(0);
              }}
              className="w-full rounded-xl border border-border bg-card px-3 py-2 text-xs sm:text-sm text-ink focus:border-herb-600 focus:outline-none focus:ring-1 focus:ring-herb-600"
            >
              <option value="all">Toutes les marques (Catalogue)</option>
              {brandsQuery.data?.map((b) => (
                <option key={b.brand} value={b.brand}>
                  {b.brand} ({b.count})
                </option>
              ))}
            </select>
          </div>
        </div>
        <SubBrandPills
          brand={selectedBrand}
          excludedSubBrands={excludedSubBrands}
          onChange={(ex) => {
            setExcludedSubBrands(ex);
            setPage(0);
          }}
        />
      </div>

      {/* Main Rows List */}
      {listQuery.isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 rounded-2xl border border-border bg-card text-ink-soft">
          <RefreshCw className="h-8 w-8 animate-spin text-herb-700" />
          <p className="mt-3 text-sm font-medium">Chargement des correspondances EAN & images...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 rounded-2xl border border-border bg-card text-ink-soft text-center p-6">
          {layer2Filter === 'pending' ? (
            <>
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mb-3" />
              <h3 className="text-base font-bold text-ink">
                Tous les produits sont validés en Couche 2 (100% vérifiés) !
              </h3>
              <p className="mt-1 text-xs text-ink-soft max-w-md">
                Aucun produit en attente pour cette sélection. Les articles validés sont automatiquement masqués de cette file et disponibles sur l'écran S19 (Prix Grossistes).
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <Link
                  to="/gtin-pricing-vat"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-herb-700 hover:bg-herb-800 text-white px-4 py-2 text-xs font-bold shadow-xs transition-colors"
                >
                  <Receipt className="h-4 w-4" />
                  Aller à S19 Prix Grossistes →
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setLayer2Filter('confirmed');
                    setPage(0);
                  }}
                >
                  <CheckCheck className="h-4 w-4 text-emerald-600" />
                  Voir les validés 100% ({kpis?.layer2ConfirmedCount ?? '—'})
                </Button>
              </div>
            </>
          ) : (
            <>
              <AlertCircle className="h-10 w-10 text-amber-500 mb-2" />
              <h3 className="text-base font-bold text-ink">Aucun produit ne correspond aux critères</h3>
              <p className="mt-1 text-xs text-ink-soft max-w-md">
                Modifiez votre recherche ou réinitialisez les filtres pour afficher d'autres articles.
              </p>
              <Button
                variant="soft"
                size="sm"
                className="mt-4"
                onClick={() => {
                  setSearch('');
                  setDebouncedSearch('');
                  setSelectedBrand('all');
                  setMatchFilter('all');
                  setLayer2Filter('pending');
                  setPage(0);
                }}
              >
                Réinitialiser les filtres
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Top Bulk Action Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-herb-600/30 bg-herb-50/60 p-3.5 shadow-2xs">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-ink">
                Page {page + 1} ({items.length} produits affichés sur {limit})
              </span>
              {unconfirmedItems.length > 0 ? (
                <span className="rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[11px] font-bold text-amber-900">
                  {unconfirmedItems.length} en attente validation Couche 2
                </span>
              ) : (
                <span className="rounded-full bg-emerald-100 border border-emerald-300 px-2 py-0.5 text-[11px] font-bold text-emerald-900 flex items-center gap-1">
                  <CheckCheck className="h-3 w-3 text-emerald-700" />
                  Tous les {items.length} produits de la page sont validés Couche 2
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Link
                to="/gtin-pricing-vat"
                className="inline-flex items-center gap-1.5 rounded-xl border border-herb-600/30 bg-white hover:bg-herb-50 px-3.5 py-1.5 text-xs font-bold text-herb-800 shadow-2xs transition-colors"
                title="Rapprocher les prix d'achat grossistes de ces produits sur S19"
              >
                <Receipt className="h-4 w-4 text-herb-700" />
                Prix grossistes (S19) →
              </Link>

              <Button
                variant={unconfirmedItems.length > 0 ? 'primary' : 'outline'}
                size="sm"
                disabled={eligibleItems.length === 0 || confirmBulkMutation.isPending}
                onClick={handleApproveAllPage}
                className="shadow-xs cursor-pointer font-bold"
              >
                <CheckCheck className={cn('h-4 w-4', confirmBulkMutation.isPending && 'animate-spin')} />
                {confirmBulkMutation.isPending
                  ? 'Validation en cours…'
                  : unconfirmedItems.length > 0
                    ? `Tout valider Couche 2 (page : ${unconfirmedItems.length})`
                    : `✓ Page validée (Couche 2)`}
              </Button>
            </div>
          </div>

          {items.map((row) => (
            <div
              key={row.id}
              className="rounded-2xl border border-border bg-card p-4 shadow-xs transition-shadow hover:shadow-md"
            >
              {/* Product Header Row */}
              <div className="border-b border-border/60 pb-3 mb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs font-semibold text-ink-ghost">#{row.id}</span>
                    {row.sku && (
                      <span className="rounded bg-cream px-1.5 py-0.5 font-mono text-[11px] text-ink-soft border border-border/80">
                        SKU: {row.sku}
                      </span>
                    )}
                    {row.brand && (
                      <span className="rounded-full bg-herb-50 border border-herb-600/20 px-2.5 py-0.5 text-xs font-bold text-herb-800">
                        {row.brand}
                      </span>
                    )}
                    {row.finalEanConfirmed ? (
                      <span
                        className="rounded-full bg-emerald-100 border border-emerald-300 px-2.5 py-0.5 text-xs font-bold text-emerald-900 flex items-center gap-1 shadow-2xs"
                        title="Double validation réussie : Fil d'attente (Couche 1) + Contrôle visuel (Couche 2). Actif sur l'écran S19 Prix Grossistes."
                      >
                        <CheckCheck className="h-3 w-3 text-emerald-700" />
                        100% Vérifié (Couche 2)
                      </span>
                    ) : (
                      <span
                        className="rounded-full bg-amber-100 border border-amber-300 px-2.5 py-0.5 text-xs font-bold text-amber-900 flex items-center gap-1 shadow-2xs"
                        title="1ère validation fil d'attente effectuée — En attente de validation visuelle (Couche 2) pour débloquer S19."
                      >
                        <Sparkles className="h-3 w-3 text-amber-700" />
                        À vérifier (Couche 2)
                      </span>
                    )}
                    {row.isVariation && (
                      <span
                        className="rounded-full bg-purple-100 border border-purple-300 px-2.5 py-0.5 text-xs font-bold text-purple-900 flex items-center gap-1"
                        title="Déclinaison / Variation de produit WooCommerce"
                      >
                        <Layers className="h-3 w-3" />
                        Variation {row.wcParentId ? `(Parent #${row.wcParentId})` : ''}
                      </span>
                    )}
                    {row.needsWooCorrection && (
                      <span
                        className="rounded-full bg-amber-100 border border-amber-400 px-2 py-0.5 text-[11px] font-bold text-amber-900"
                        title="Validé avec un code différent du site : envoyé vers À corriger (Woo)"
                      >
                        En attente d'envoi Woo
                      </span>
                    )}
                    {row.isTopface && (
                      <span className="rounded-full bg-amber-100 border border-amber-400 px-2 py-0.5 text-[11px] font-bold text-amber-900">
                        ⭐ Gamme Topface
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setZoomItem(row)}
                      className="inline-flex items-center gap-1 rounded-lg border border-border bg-cream/60 px-2 py-1 text-xs font-semibold text-ink-soft hover:bg-cream hover:text-ink transition-colors"
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                      Vue comparative HD
                    </button>
                  </div>
                </div>

                {/* Full Un-shrinked Product Name */}
                <h3 className="mt-2 text-base sm:text-lg font-bold text-ink leading-snug break-words">
                  {row.name}
                </h3>

                {/* Divergence warning banner (e.g. Uriage vs GUM dentifrice) */}
                {row.hasDivergence && (
                  <div className="mt-2.5 flex items-center justify-between rounded-xl border border-rose-300 bg-rose-50/90 p-2.5 text-xs text-rose-900 shadow-2xs">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" />
                      <div>
                        <span className="font-bold">⚠️ Alerte Divergence : </span>
                        <span>
                          {row.divergenceReason || "Le produit de notre catalogue semble différer des offres concurrentes détectées pour ce code-barres."}
                        </span>
                      </div>
                    </div>
                    <span className="shrink-0 font-mono text-[11px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-md border border-rose-200">
                      Code Suspect
                    </span>
                  </div>
                )}
              </div>

              {/* Action feedback banner if applicable */}
              {actionFeedback?.productId === row.id && (
                <div
                  className={cn(
                    'mb-3 rounded-lg px-3 py-2 text-xs font-semibold flex items-center justify-between border',
                    actionFeedback.type === 'confirm'
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                      : 'bg-rose-50 border-rose-300 text-rose-900'
                  )}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {actionFeedback.type === 'confirm' ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-rose-600 shrink-0" />
                    )}
                    <span>{actionFeedback.message}</span>
                    {actionFeedback.targetGtin && (
                      <Link
                        to={`/gtin-pricing-vat?q=${encodeURIComponent(actionFeedback.targetGtin)}`}
                        className="ml-2 inline-flex items-center gap-1 font-bold underline hover:text-emerald-950"
                      >
                        Rapprocher le prix d'achat sur S19 →
                      </Link>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setActionFeedback(null)}
                    className="text-ink-ghost hover:text-ink"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* Grid: 3 Main Columns + Optional 4th Topface Column */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {/* 1. Notre Produit (MaPara) */}
                <div className="flex gap-4 rounded-xl border border-border/80 bg-paper/60 p-3">
                  {/* Big Image Ours */}
                  <div className="relative group shrink-0">
                    {row.images[0] ? (
                      <img
                        src={row.images[0]}
                        alt={row.name}
                        className="h-36 w-36 sm:h-40 sm:w-40 rounded-xl object-contain bg-white border border-border p-1 shadow-2xs"
                        loading="lazy"
                        onError={(e) => {
                          (e.target as HTMLElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="flex h-36 w-36 sm:h-40 sm:w-40 items-center justify-center rounded-xl bg-card border border-border text-xs text-ink-ghost font-medium">
                        Sans image
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setZoomItem(row)}
                      className="absolute inset-0 flex items-center justify-center rounded-xl bg-ink/40 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Details Ours */}
                  <div className="flex flex-1 flex-col justify-between min-w-0">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-herb-700">
                        Notre Catalogue
                      </span>
                      <h4 className="mt-0.5 text-sm font-bold text-ink leading-snug break-words" title={row.name}>
                        {row.name}
                      </h4>

                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[11px] font-medium text-ink-ghost">EAN :</span>
                          {row.gtin || row.barcodeRaw || row.eanRaw ? (
                            <span className="rounded bg-emerald-100 border border-emerald-500/30 px-2 py-0.5 font-mono text-xs font-bold text-emerald-900">
                              {row.gtin || row.barcodeRaw || row.eanRaw}
                            </span>
                          ) : (
                            <span className="rounded bg-rose-100 border border-rose-300 px-2 py-0.5 font-mono text-xs font-bold text-rose-800">
                              Non renseigné
                            </span>
                          )}
                        </div>

                        {row.regularPriceTtc !== null && (
                          <div className="text-xs text-ink-soft">
                            Prix public : <span className="font-bold text-ink">{formatTnd(row.regularPriceTtc)}</span>
                          </div>
                        )}

                        {/* Buying Price & Wholesaler Buy-Box match */}
                        <div className="pt-2 border-t border-border/40 text-xs space-y-1.5">
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-[11px] text-ink-ghost font-medium">Prix d'achat HT :</span>
                            {row.purchasePriceHt !== null ? (
                              <span className="font-mono font-bold text-ink">
                                {formatTnd(row.purchasePriceHt)} <span className="text-[10px] text-ink-ghost font-normal">HT</span>
                              </span>
                            ) : (
                              <span className="rounded bg-amber-50 border border-amber-300 px-1.5 py-0.2 text-[10px] font-bold text-amber-800">
                                Non renseigné
                              </span>
                            )}
                          </div>

                          {row.wholesalerBuyBox && (
                            <div className="rounded-lg border border-herb-600/30 bg-herb-50/70 p-2 text-xs">
                              <div className="flex flex-wrap items-center justify-between gap-1">
                                <span className="text-[11px] font-semibold text-herb-900 flex items-center gap-1">
                                  <Sparkles className="h-3 w-3 text-amber-500 shrink-0" />
                                  Grossiste ({row.wholesalerBuyBox.sourceLabel}) :
                                </span>
                                <span className="font-mono font-bold text-herb-900 text-xs shrink-0">
                                  {formatTnd(row.wholesalerBuyBox.priceHt)} HT
                                </span>
                              </div>
                              <div className="mt-1.5 flex items-center justify-between gap-2">
                                {row.purchasePriceHt !== row.wholesalerBuyBox.priceHt ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      adoptWholesalerPriceMutation.mutate({
                                        productId: row.id,
                                        offerId: row.wholesalerBuyBox!.offerId,
                                        priceHt: row.wholesalerBuyBox!.priceHt,
                                        sourceKey: row.wholesalerBuyBox!.sourceKey,
                                      })
                                    }
                                    disabled={adoptWholesalerPriceMutation.isPending}
                                    className="inline-flex items-center gap-1 rounded bg-herb-700 hover:bg-herb-800 text-white px-2 py-1 text-[10px] font-semibold cursor-pointer shadow-2xs"
                                    title="Adopter immédiatement ce prix d'achat grossiste"
                                  >
                                    Adopter ({formatTnd(row.wholesalerBuyBox.priceHt)})
                                  </button>
                                ) : (
                                  <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded border border-emerald-300">
                                    ✓ Prix actif
                                  </span>
                                )}
                                <Link
                                  to={`/gtin-pricing-vat?q=${encodeURIComponent(row.gtin || row.sku || row.name)}`}
                                  className="text-[10px] font-semibold text-herb-800 hover:text-herb-950 hover:underline flex items-center gap-0.5"
                                >
                                  S19 <ChevronRight className="h-3 w-3" />
                                </Link>
                              </div>
                            </div>
                          )}

                          {!row.wholesalerBuyBox && (
                            <div className="flex items-center justify-end">
                              <Link
                                to={`/gtin-pricing-vat?q=${encodeURIComponent(row.gtin || row.sku || row.name)}`}
                                className="text-[10px] font-semibold text-herb-800 hover:text-herb-950 hover:underline flex items-center gap-0.5"
                              >
                                Rapprocher sur S19 <ChevronRight className="h-3 w-3" />
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="pt-2.5 border-t border-border/50 flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        {row.isApproved ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 border border-emerald-500/30 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                            <Check className="h-3 w-3" /> Approuvé Workbench
                          </span>
                        ) : row.reviewStatus === 'rejected' ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-rose-50 border border-rose-300 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
                            <X className="h-3 w-3" /> Rejeté
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-300 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                            En révision
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Link
                          to={`/gtin-pricing-vat?q=${encodeURIComponent(row.gtin || row.sku || row.name)}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-herb-600/30 bg-white hover:bg-herb-50 text-herb-800 text-xs font-semibold transition-colors cursor-pointer"
                          title="Rapprocher le prix d'achat grossiste sur S19"
                        >
                          <Receipt className="h-3.5 w-3.5 text-herb-700" />
                          Prix S19 →
                        </Link>

                        <button
                          type="button"
                          onClick={() => {
                            if (row.finalEanConfirmed) {
                              if (
                                window.confirm(
                                  `Annuler la validation Couche 2 pour "${row.name}" ?\n\nLe produit retournera dans l'onglet "À valider" et sera retiré de l'écran S19 jusqu'à nouvelle confirmation.`,
                                )
                              ) {
                                unconfirmMutation.mutate(row.id);
                              }
                              return;
                            }
                            const targetGtin = getCandidateGtin(row);
                            if (!targetGtin) {
                              alert('Aucun code EAN disponible à valider pour ce produit.');
                              return;
                            }
                            confirmMutation.mutate({ productId: row.id, gtin: targetGtin });
                          }}
                          disabled={confirmMutation.isPending || unconfirmMutation.isPending || rejectMutation.isPending || hideMutation.isPending}
                          className={cn(
                            'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold shadow-2xs transition-colors cursor-pointer',
                            row.finalEanConfirmed
                              ? 'bg-emerald-100 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 text-emerald-900 border border-emerald-300'
                              : 'bg-emerald-600 hover:bg-emerald-700 text-white',
                          )}
                          title={
                            row.finalEanConfirmed
                              ? 'Validé en Couche 2 (S19). Cliquer pour annuler la validation et le renvoyer en attente.'
                              : 'Valider la Couche 2 (double vérification visuelle 100% garantie pour S19)'
                          }
                        >
                          <CheckCheck className="h-3.5 w-3.5" />
                          {row.finalEanConfirmed ? '✓ Validé (Couche 2)' : 'Confirmer (Couche 2)'}
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Générer un code EAN-13 interne unique (format 200...) pour "${row.name}" ?\n\nRecommandé pour les déclinaisons/variantes (coloris, garçon/fille, nuance) afin d'éviter tout conflit de doublon code-barres.`,
                              )
                            ) {
                              confirmMutation.mutate({ productId: row.id, generateInternal: true });
                            }
                          }}
                          disabled={confirmMutation.isPending || rejectMutation.isPending || hideMutation.isPending}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-indigo-200 bg-indigo-50/70 hover:bg-indigo-100 text-indigo-800 text-xs font-semibold transition-colors cursor-pointer"
                          title="Générer un code EAN-13 interne unique (Préfixe 2) pour ce produit ou sa variante"
                        >
                          <Boxes className="h-3.5 w-3.5 text-indigo-600" />
                          Code interne
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Rejeter le produit #${row.id} "${row.name}" ? Il sera renvoyé dans la file de révision Workbench pour être rapproché correctement.`,
                              )
                            ) {
                              rejectMutation.mutate({ productId: row.id });
                            }
                          }}
                          disabled={confirmMutation.isPending || rejectMutation.isPending || hideMutation.isPending}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 text-xs font-semibold transition-colors cursor-pointer"
                          title="Rejeter ce rapprochement — renvoie le produit dans la file de révision GTIN Workbench"
                        >
                          <X className="h-3.5 w-3.5" />
                          Rejeter
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Masquer le produit #${row.id} "${row.name}" ? Il sera masqué de l'application et de WooCommerce.`,
                              )
                            ) {
                              hideMutation.mutate(row.id);
                            }
                          }}
                          disabled={confirmMutation.isPending || rejectMutation.isPending || hideMutation.isPending}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-border bg-white hover:bg-cream text-ink-soft hover:text-ink text-xs font-semibold transition-colors cursor-pointer"
                          title="Masquer le produit dans l'application et sur WooCommerce"
                        >
                          <EyeOff className="h-3.5 w-3.5" />
                          Masquer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Pharmashop */}
                <div
                  className={cn(
                    'flex gap-4 rounded-xl border p-3 transition-colors',
                    row.pharmashop
                      ? 'border-sky-200 bg-sky-50/30'
                      : 'border-dashed border-border bg-card/40',
                  )}
                >
                  {row.pharmashop ? (
                    <>
                      {/* Big Image Pharmashop */}
                      <div className="relative group shrink-0">
                        {row.pharmashop.image ? (
                          <img
                            src={row.pharmashop.image}
                            alt={row.pharmashop.name}
                            className="h-36 w-36 sm:h-40 sm:w-40 rounded-xl object-contain bg-white border border-border p-1 shadow-2xs"
                            loading="lazy"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="flex h-36 w-36 sm:h-40 sm:w-40 items-center justify-center rounded-xl bg-card border border-border text-xs text-ink-ghost font-medium">
                            Sans image
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setZoomItem(row)}
                          className="absolute inset-0 flex items-center justify-center rounded-xl bg-ink/40 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                      </div>

                      {/* Details Pharmashop */}
                      <div className="flex flex-1 flex-col justify-between min-w-0">
                        <div>
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-sky-800">
                              Pharma-Shop.tn
                            </span>
                            {row.pharmashop.gtinMatchesOurs ? (
                              <span className="rounded-full bg-emerald-100 px-1.5 py-0.2 text-[10px] font-bold text-emerald-800 border border-emerald-400">
                                ✓ EAN 100%
                              </span>
                            ) : (
                              <span className="rounded-full bg-sky-100 px-1.5 py-0.2 text-[10px] font-bold text-sky-800 border border-sky-300">
                                Nom {row.pharmashop.score}%
                              </span>
                            )}
                          </div>

                          <h4
                            className="mt-0.5 text-xs sm:text-sm font-semibold text-ink leading-snug break-words"
                            title={row.pharmashop.name}
                          >
                            {row.pharmashop.name}
                          </h4>

                          <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[11px] font-medium text-ink-ghost">EAN :</span>
                              {row.pharmashop.gtin ? (
                                <span
                                  className={cn(
                                    'rounded px-1.5 py-0.2 font-mono text-xs font-bold',
                                    row.pharmashop.gtinMatchesOurs
                                      ? 'bg-emerald-100 text-emerald-900 border border-emerald-400'
                                      : 'bg-amber-100 text-amber-900 border border-amber-300',
                                  )}
                                >
                                  {row.pharmashop.gtin}
                                </span>
                              ) : (
                                <span className="text-[11px] text-ink-ghost italic">Non publié</span>
                              )}
                            </div>

                            {row.pharmashop.priceTtc !== null && (
                              <div className="flex flex-wrap items-center gap-1.5 text-xs text-ink-soft">
                                <span>Prix : <span className="font-bold text-ink">{formatTnd(row.pharmashop.priceTtc)}</span></span>
                                {row.regularPriceTtc !== null && row.regularPriceTtc > 0 && (() => {
                                  const diff = row.pharmashop.priceTtc - row.regularPriceTtc;
                                  const diffPct = Math.round((diff / row.regularPriceTtc) * 100);
                                  if (Math.abs(diff) < 50) {
                                    return (
                                      <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.2 text-[10px] font-semibold text-slate-700 dark:text-slate-300">
                                        = Même prix
                                      </span>
                                    );
                                  }
                                  if (diff > 0) {
                                    return (
                                      <span className="rounded bg-amber-100 dark:bg-amber-950/50 px-1.5 py-0.2 text-[10px] font-bold text-amber-800 dark:text-amber-300 border border-amber-300/50" title="Concurrent plus cher">
                                        +{formatTnd(diff)} (+{diffPct}%)
                                      </span>
                                    );
                                  }
                                  return (
                                    <span className="rounded bg-rose-100 dark:bg-rose-950/50 px-1.5 py-0.2 text-[10px] font-bold text-rose-800 dark:text-rose-300 border border-rose-300/50" title="Concurrent moins cher">
                                      {formatTnd(diff)} ({diffPct}%)
                                    </span>
                                  );
                                })()}
                              </div>
                            )}

                            {row.pharmashop.inStock !== null && (
                              <div className="text-[11px]">
                                {row.pharmashop.inStock ? (
                                  <span className="text-emerald-700 font-medium">● En stock</span>
                                ) : (
                                  <span className="text-rose-700 font-medium">○ Épuisé</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {row.pharmashop.url && (
                          <div className="pt-2 border-t border-border/40">
                            <a
                              href={row.pharmashop.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 hover:text-sky-900 hover:underline"
                            >
                              Voir sur Pharma-Shop <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center p-4 text-center">
                      <span className="text-xs font-bold text-ink-ghost">Non référencé sur Pharma-Shop</span>
                      <p className="mt-1 text-[11px] text-ink-ghost/80">Aucune offre EAN ou nom correspondante</p>
                    </div>
                  )}
                </div>

                {/* 3. Parashop */}
                <div
                  className={cn(
                    'flex gap-4 rounded-xl border p-3 transition-colors',
                    row.parashop
                      ? 'border-emerald-200 bg-emerald-50/30'
                      : 'border-dashed border-border bg-card/40',
                  )}
                >
                  {row.parashop ? (
                    <>
                      {/* Big Image Parashop */}
                      <div className="relative group shrink-0">
                        {row.parashop.image ? (
                          <img
                            src={row.parashop.image}
                            alt={row.parashop.name}
                            className="h-36 w-36 sm:h-40 sm:w-40 rounded-xl object-contain bg-white border border-border p-1 shadow-2xs"
                            loading="lazy"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="flex h-36 w-36 sm:h-40 sm:w-40 items-center justify-center rounded-xl bg-card border border-border text-xs text-ink-ghost font-medium">
                            Sans image
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setZoomItem(row)}
                          className="absolute inset-0 flex items-center justify-center rounded-xl bg-ink/40 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                      </div>

                      {/* Details Parashop */}
                      <div className="flex flex-1 flex-col justify-between min-w-0">
                        <div>
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                              Parashop.tn
                            </span>
                            {row.parashop.gtinMatchesOurs ? (
                              <span className="rounded-full bg-emerald-100 px-1.5 py-0.2 text-[10px] font-bold text-emerald-800 border border-emerald-400">
                                ✓ EAN 100%
                              </span>
                            ) : (
                              <span className="rounded-full bg-emerald-100 px-1.5 py-0.2 text-[10px] font-bold text-emerald-800 border border-emerald-300">
                                Nom {row.parashop.score}%
                              </span>
                            )}
                          </div>

                          <h4
                            className="mt-0.5 text-xs sm:text-sm font-semibold text-ink leading-snug break-words"
                            title={row.parashop.name}
                          >
                            {row.parashop.name}
                          </h4>

                          <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[11px] font-medium text-ink-ghost">EAN :</span>
                              {row.parashop.gtin ? (
                                <span
                                  className={cn(
                                    'rounded px-1.5 py-0.2 font-mono text-xs font-bold',
                                    row.parashop.gtinMatchesOurs
                                      ? 'bg-emerald-100 text-emerald-900 border border-emerald-400'
                                      : 'bg-amber-100 text-amber-900 border border-amber-300',
                                  )}
                                >
                                  {row.parashop.gtin}
                                </span>
                              ) : (
                                <span className="text-[11px] text-ink-ghost italic">Non publié</span>
                              )}
                            </div>

                            {row.parashop.priceTtc !== null && (
                              <div className="flex flex-wrap items-center gap-1.5 text-xs text-ink-soft">
                                <span>Prix : <span className="font-bold text-ink">{formatTnd(row.parashop.priceTtc)}</span></span>
                                {row.regularPriceTtc !== null && row.regularPriceTtc > 0 && (() => {
                                  const diff = row.parashop.priceTtc - row.regularPriceTtc;
                                  const diffPct = Math.round((diff / row.regularPriceTtc) * 100);
                                  if (Math.abs(diff) < 50) {
                                    return (
                                      <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.2 text-[10px] font-semibold text-slate-700 dark:text-slate-300">
                                        = Même prix
                                      </span>
                                    );
                                  }
                                  if (diff > 0) {
                                    return (
                                      <span className="rounded bg-amber-100 dark:bg-amber-950/50 px-1.5 py-0.2 text-[10px] font-bold text-amber-800 dark:text-amber-300 border border-amber-300/50" title="Concurrent plus cher">
                                        +{formatTnd(diff)} (+{diffPct}%)
                                      </span>
                                    );
                                  }
                                  return (
                                    <span className="rounded bg-rose-100 dark:bg-rose-950/50 px-1.5 py-0.2 text-[10px] font-bold text-rose-800 dark:text-rose-300 border border-rose-300/50" title="Concurrent moins cher">
                                      {formatTnd(diff)} ({diffPct}%)
                                    </span>
                                  );
                                })()}
                              </div>
                            )}

                            {row.parashop.inStock !== null && (
                              <div className="text-[11px]">
                                {row.parashop.inStock ? (
                                  <span className="text-emerald-700 font-medium">● En stock</span>
                                ) : (
                                  <span className="text-rose-700 font-medium">○ Épuisé</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {row.parashop.url && (
                          <div className="pt-2 border-t border-border/40">
                            <a
                              href={row.parashop.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 hover:underline"
                            >
                              Voir sur Parashop <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center p-4 text-center">
                      <span className="text-xs font-bold text-ink-ghost">Non référencé sur Parashop</span>
                      <p className="mt-1 text-[11px] text-ink-ghost/80">Aucune offre EAN ou nom correspondante</p>
                    </div>
                  )}
                </div>
              </div>

              {/* 4. Topface Price List Integration Banner (When Product is Topface) */}
              {row.isTopface && (
                <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50/60 p-3.5 shadow-2xs">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-amber-200/80 text-amber-900">
                        <FileSpreadsheet className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold uppercase tracking-wider text-amber-950">
                            Rapprochement Tarif Fournisseur Topface (Fichier Grossiste)
                          </span>
                          {row.topfaceMatch && (
                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                row.topfaceMatch.matchType === 'gtin' || row.topfaceMatch.isApproved
                                  ? 'bg-emerald-100 text-emerald-900 border border-emerald-300'
                                  : 'bg-amber-200 text-amber-900'
                              }`}
                            >
                              {row.topfaceMatch.matchType === 'gtin' || row.topfaceMatch.isApproved
                                ? 'Match EAN exact (100%)'
                                : `Confiance nom: ${row.topfaceMatch.score}%`}
                            </span>
                          )}
                        </div>

                        {row.topfaceMatch ? (
                          <div className="mt-1 text-xs text-amber-900">
                            <p className="font-semibold text-amber-950">{row.topfaceMatch.name}</p>
                            <div className="mt-1 flex flex-wrap items-center gap-3">
                              <span>
                                Code-barres tarif :{' '}
                                <strong className="font-mono text-sm text-amber-950 bg-white/80 px-1.5 py-0.2 rounded border border-amber-300">
                                  {row.topfaceMatch.barcode}
                                </strong>
                              </span>
                              {row.topfaceMatch.priceHt !== null && (
                                <span>
                                  Prix Négoce HT :{' '}
                                  <strong>{formatTnd(row.topfaceMatch.priceHt)}</strong>
                                </span>
                              )}
                              {row.topfaceMatch.reference && (
                                <span>
                                  Réf : <strong>{row.topfaceMatch.reference}</strong>
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="mt-1 text-xs text-amber-800">
                            {row.gtin || row.barcodeRaw || row.eanRaw
                              ? 'Aucune référence avec le même EAN trouvée dans le tarif Topface.'
                              : 'Aucune correspondance automatique évidente trouvée dans le tarif. Vous pouvez rechercher manuellement.'}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                      {row.topfaceMatch && !row.topfaceMatch.isApproved && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            applyEanMutation.mutate({
                              productId: row.id,
                              gtin: row.topfaceMatch!.barcode,
                              offerId: row.topfaceMatch!.offerId,
                            })
                          }
                          disabled={applyEanMutation.isPending}
                        >
                          <Barcode className="h-3.5 w-3.5" />
                          Appliquer cet EAN ({row.topfaceMatch.barcode})
                        </Button>
                      )}

                      {row.topfaceMatch?.isApproved && (
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-100 border border-emerald-400 px-3 py-1.5 text-xs font-bold text-emerald-900">
                          <Check className="h-4 w-4" /> EAN Tarif validé
                        </span>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          setTopfaceModalProduct(row);
                          setTopfaceCustomSearch(row.name);
                        }}
                        className="rounded-lg border border-amber-300 bg-white/80 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-white transition-colors"
                      >
                        Rechercher autre réf tarif...
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Bottom Bulk Action Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-herb-600/30 bg-herb-50/60 p-3.5 shadow-2xs">
            <div className="flex items-center gap-2 flex-wrap text-xs text-ink-soft">
              <span>Validation visuelle grand format (Couche 2) :</span>
              {unconfirmedItems.length > 0 ? (
                <span className="font-bold text-amber-900">
                  {unconfirmedItems.length} produit(s) en attente de confirmation Couche 2
                </span>
              ) : (
                <span className="font-bold text-emerald-900 flex items-center gap-1">
                  <CheckCheck className="h-3.5 w-3.5 text-emerald-700" />
                  Tous les {items.length} produits sont confirmés Couche 2
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Link
                to="/gtin-pricing-vat"
                className="inline-flex items-center gap-1.5 rounded-xl border border-herb-600/30 bg-white hover:bg-herb-50 px-3.5 py-1.5 text-xs font-bold text-herb-800 shadow-2xs transition-colors"
                title="Rapprocher les prix d'achat grossistes de ces produits sur S19"
              >
                <Receipt className="h-4 w-4 text-herb-700" />
                Prix grossistes (S19) →
              </Link>

              <Button
                variant={unconfirmedItems.length > 0 ? 'primary' : 'outline'}
                size="sm"
                disabled={eligibleItems.length === 0 || confirmBulkMutation.isPending}
                onClick={handleApproveAllPage}
                className="shadow-xs cursor-pointer font-bold"
              >
                <CheckCheck className={cn('h-4 w-4', confirmBulkMutation.isPending && 'animate-spin')} />
                {confirmBulkMutation.isPending
                  ? 'Validation en cours…'
                  : unconfirmedItems.length > 0
                    ? `Tout valider Couche 2 (page : ${unconfirmedItems.length})`
                    : `✓ Page validée (Couche 2)`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 shadow-2xs">
          <p className="text-xs text-ink-soft">
            Affichage de <strong className="text-ink">{page * limit + 1}</strong> à{' '}
            <strong className="text-ink">{Math.min(total, (page + 1) * limit)}</strong> sur{' '}
            <strong className="text-ink">{total.toLocaleString('fr-FR')}</strong> produits
          </p>

          <div className="flex items-center gap-2">
            <Button
              variant="soft"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" /> Précédent
            </Button>

            <span className="font-mono text-xs font-bold text-ink px-2">
              Page {page + 1} / {totalPages}
            </span>

            <Button
              variant="soft"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              Suivant <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* HD Side-by-Side Zoom Modal (Portaled to document.body for true viewport centering) */}
      {zoomItem &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/75 backdrop-blur-xs p-4 sm:p-6 overflow-y-auto animate-fade"
            onClick={() => setZoomItem(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Comparaison Visuelle Grand Format"
              className="relative w-full max-w-5xl rounded-3xl border border-border bg-card p-6 shadow-2xl max-h-[90vh] overflow-y-auto m-auto animate-rise"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setZoomItem(null)}
                className="absolute right-4 top-4 rounded-full p-2 text-ink-ghost hover:bg-cream hover:text-ink transition-colors cursor-pointer"
                title="Fermer (Échap)"
              >
                <X className="h-5 w-5" />
              </button>

              <div>
                <span className="rounded-full bg-herb-100 px-2 py-0.5 text-xs font-bold text-herb-800">
                  Comparaison Visuelle Grand Format
                </span>
                <h2 className="mt-1 text-xl font-bold text-ink font-display">{zoomItem.name}</h2>
                <div className="mt-1 flex items-center gap-3 text-xs text-ink-soft">
                  <span>Marque : <strong>{zoomItem.brand ?? '—'}</strong></span>
                  <span>GTIN Catalogue : <strong className="font-mono text-emerald-700">{zoomItem.gtin ?? zoomItem.barcodeRaw ?? zoomItem.eanRaw ?? 'Non défini'}</strong></span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Notre Packshot */}
                <div className="rounded-2xl border border-border bg-paper p-4 flex flex-col items-center text-center">
                  <span className="text-xs font-bold text-herb-700 uppercase tracking-wider mb-2">Notre Produit (MaPara)</span>
                  {zoomItem.images[0] ? (
                    <img
                      src={zoomItem.images[0]}
                      alt={zoomItem.name}
                      className="h-64 w-64 rounded-xl object-contain bg-white border border-border p-2 shadow-sm"
                    />
                  ) : (
                    <div className="h-64 w-64 rounded-xl bg-card border border-border flex items-center justify-center text-xs text-ink-ghost">
                      Sans image
                    </div>
                  )}
                  <p className="mt-3 text-xs font-bold text-ink break-words">{zoomItem.name}</p>
                  <span className="mt-1 font-mono text-xs text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-300">
                    EAN: {zoomItem.gtin ?? zoomItem.barcodeRaw ?? zoomItem.eanRaw ?? '—'}
                  </span>
                  {zoomItem.regularPriceTtc !== null && (
                    <span className="mt-1 text-xs text-ink-soft">
                      Prix public : <strong className="text-ink">{formatTnd(zoomItem.regularPriceTtc)}</strong>
                    </span>
                  )}
                </div>

                {/* Pharma-Shop Packshot */}
                <div className="rounded-2xl border border-border bg-sky-50/40 p-4 flex flex-col items-center text-center">
                  <span className="text-xs font-bold text-sky-800 uppercase tracking-wider mb-2">Pharma-Shop.tn</span>
                  {zoomItem.pharmashop?.image ? (
                    <img
                      src={zoomItem.pharmashop.image}
                      alt={zoomItem.pharmashop.name}
                      className="h-64 w-64 rounded-xl object-contain bg-white border border-border p-2 shadow-sm"
                    />
                  ) : (
                    <div className="h-64 w-64 rounded-xl bg-card border border-border flex items-center justify-center text-xs text-ink-ghost">
                      {zoomItem.pharmashop ? 'Sans image' : 'Non référencé'}
                    </div>
                  )}
                  <p className="mt-3 text-xs font-bold text-ink break-words">
                    {zoomItem.pharmashop?.name ?? 'Non disponible'}
                  </p>
                  {zoomItem.pharmashop && (
                    <>
                      <span className="mt-1 font-mono text-xs text-sky-900 bg-sky-100 px-2 py-0.5 rounded border border-sky-300">
                        EAN: {zoomItem.pharmashop.gtin ?? 'Non publié'}
                      </span>
                      {zoomItem.pharmashop.priceTtc !== null && (
                        <span className="mt-1 flex flex-wrap items-center justify-center gap-1 text-xs text-ink-soft">
                          <span>Prix : <strong className="text-ink">{formatTnd(zoomItem.pharmashop.priceTtc)}</strong></span>
                          {zoomItem.regularPriceTtc !== null && zoomItem.regularPriceTtc > 0 && (() => {
                            const diff = zoomItem.pharmashop.priceTtc - zoomItem.regularPriceTtc;
                            const diffPct = Math.round((diff / zoomItem.regularPriceTtc) * 100);
                            if (Math.abs(diff) < 50) return null;
                            return (
                              <span className={cn('rounded px-1.5 py-0.2 text-[10px] font-bold', diff > 0 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800')}>
                                {diff > 0 ? `+${formatTnd(diff)} (+${diffPct}%)` : `${formatTnd(diff)} (${diffPct}%)`}
                              </span>
                            );
                          })()}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* Parashop Packshot */}
                <div className="rounded-2xl border border-border bg-emerald-50/40 p-4 flex flex-col items-center text-center">
                  <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2">Parashop.tn</span>
                  {zoomItem.parashop?.image ? (
                    <img
                      src={zoomItem.parashop.image}
                      alt={zoomItem.parashop.name}
                      className="h-64 w-64 rounded-xl object-contain bg-white border border-border p-2 shadow-sm"
                    />
                  ) : (
                    <div className="h-64 w-64 rounded-xl bg-card border border-border flex items-center justify-center text-xs text-ink-ghost">
                      {zoomItem.parashop ? 'Sans image' : 'Non référencé'}
                    </div>
                  )}
                  <p className="mt-3 text-xs font-bold text-ink break-words">
                    {zoomItem.parashop?.name ?? 'Non disponible'}
                  </p>
                  {zoomItem.parashop && (
                    <>
                      <span className="mt-1 font-mono text-xs text-emerald-900 bg-emerald-100 px-2 py-0.5 rounded border border-emerald-300">
                        EAN: {zoomItem.parashop.gtin ?? 'Non publié'}
                      </span>
                      {zoomItem.parashop.priceTtc !== null && (
                        <span className="mt-1 flex flex-wrap items-center justify-center gap-1 text-xs text-ink-soft">
                          <span>Prix : <strong className="text-ink">{formatTnd(zoomItem.parashop.priceTtc)}</strong></span>
                          {zoomItem.regularPriceTtc !== null && zoomItem.regularPriceTtc > 0 && (() => {
                            const diff = zoomItem.parashop.priceTtc - zoomItem.regularPriceTtc;
                            const diffPct = Math.round((diff / zoomItem.regularPriceTtc) * 100);
                            if (Math.abs(diff) < 50) return null;
                            return (
                              <span className={cn('rounded px-1.5 py-0.2 text-[10px] font-bold', diff > 0 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800')}>
                                {diff > 0 ? `+${formatTnd(diff)} (+${diffPct}%)` : `${formatTnd(diff)} (${diffPct}%)`}
                              </span>
                            );
                          })()}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (zoomItem.finalEanConfirmed) {
                        if (
                          window.confirm(
                            `Annuler la validation Couche 2 pour "${zoomItem.name}" ?\n\nLe produit retournera dans l'onglet "À valider" et sera retiré de l'écran S19 jusqu'à nouvelle confirmation.`,
                          )
                        ) {
                          unconfirmMutation.mutate(zoomItem.id);
                        }
                        return;
                      }
                      const targetGtin = zoomItem.gtin || zoomItem.barcodeRaw || zoomItem.eanRaw || zoomItem.pharmashop?.gtin || zoomItem.parashop?.gtin;
                      if (!targetGtin) {
                        alert('Aucun code EAN disponible à valider.');
                        return;
                      }
                      confirmMutation.mutate({ productId: zoomItem.id, gtin: targetGtin });
                    }}
                    disabled={confirmMutation.isPending || unconfirmMutation.isPending || rejectMutation.isPending}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold shadow-xs transition-colors cursor-pointer',
                      zoomItem.finalEanConfirmed
                        ? 'bg-emerald-100 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 text-emerald-900 border border-emerald-300'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white',
                    )}
                    title={
                      zoomItem.finalEanConfirmed
                        ? 'Validé en Couche 2 (S19). Cliquer pour annuler la validation.'
                        : 'Confirmer et valider en Couche 2'
                    }
                  >
                    <CheckCheck className="h-4 w-4" />
                    {zoomItem.finalEanConfirmed ? '✓ Validé (Couche 2)' : 'Confirmer / Valider Couche 2'}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Générer un code EAN-13 interne unique (format 200...) pour "${zoomItem.name}" ?\n\nRecommandé pour les déclinaisons/variantes (coloris, garçon/fille, nuance) afin d'éviter tout conflit de doublon code-barres.`,
                        )
                      ) {
                        confirmMutation.mutate({ productId: zoomItem.id, generateInternal: true });
                      }
                    }}
                    disabled={confirmMutation.isPending || rejectMutation.isPending}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-indigo-200 bg-indigo-50/80 hover:bg-indigo-100 text-indigo-800 text-xs font-semibold transition-colors cursor-pointer"
                    title="Générer un code EAN-13 interne unique (Préfixe 2) pour ce produit ou sa variante"
                  >
                    <Boxes className="h-4 w-4 text-indigo-600" />
                    Code interne (Variante)
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Rejeter le produit #${zoomItem.id} "${zoomItem.name}" ? Il sera renvoyé dans la file de révision Workbench.`)) {
                        rejectMutation.mutate({ productId: zoomItem.id });
                      }
                    }}
                    disabled={confirmMutation.isPending || rejectMutation.isPending}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 text-xs font-semibold transition-colors cursor-pointer"
                  >
                    <X className="h-4 w-4" />
                    Rejeter (Renvoyer en révision)
                  </button>
                </div>

                <Button variant="soft" onClick={() => setZoomItem(null)}>
                  Fermer
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Topface Price List Selector Modal (Portaled to document.body for true viewport centering) */}
      {topfaceModalProduct &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/75 backdrop-blur-xs p-4 sm:p-6 overflow-y-auto animate-fade"
            onClick={() => setTopfaceModalProduct(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Tarif Fournisseur Topface"
              className="relative w-full max-w-2xl rounded-3xl border border-amber-300 bg-card p-6 shadow-2xl max-h-[90vh] overflow-y-auto m-auto animate-rise"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setTopfaceModalProduct(null)}
                className="absolute right-4 top-4 rounded-full p-2 text-ink-ghost hover:bg-cream hover:text-ink transition-colors cursor-pointer"
                title="Fermer (Échap)"
              >
                <X className="h-5 w-5" />
              </button>

              <div>
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-amber-700" />
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-900">
                    Tarif Fournisseur Topface
                  </span>
                </div>
                <h3 className="mt-2 text-lg font-bold text-ink">{topfaceModalProduct.name}</h3>
                <p className="text-xs text-ink-soft">
                  Sélectionnez le produit correspondant dans le tarif officiel Topface pour en adopter l'EAN.
                </p>
              </div>

              <div className="mt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-ghost" />
                  <input
                    type="text"
                    placeholder="Rechercher dans le fichier tarif Topface..."
                    value={topfaceCustomSearch}
                    onChange={(e) => setTopfaceCustomSearch(e.target.value)}
                    className="w-full rounded-xl border border-border bg-card pl-9 pr-4 py-2 text-xs sm:text-sm text-ink focus:border-amber-600 focus:outline-none focus:ring-1 focus:ring-amber-600"
                  />
                </div>
              </div>

              <div className="mt-4 divide-y divide-border/60 max-h-96 overflow-y-auto rounded-xl border border-border">
                {suggestionsQuery.isLoading ? (
                  <div className="py-8 text-center text-xs text-ink-soft">
                    <RefreshCw className="h-5 w-5 animate-spin mx-auto text-amber-600 mb-2" />
                    Recherche dans le tarif...
                  </div>
                ) : (suggestionsQuery.data?.suggestions ?? []).length === 0 ? (
                  <div className="py-8 text-center text-xs text-ink-soft">
                    Aucune référence trouvée dans le tarif Topface.
                  </div>
                ) : (
                  suggestionsQuery.data?.suggestions.map((sug) => (
                    <div
                      key={sug.id}
                      className="flex items-center justify-between p-3 hover:bg-amber-50/40 transition-colors"
                    >
                      <div className="min-w-0 flex-1 pr-3">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-bold text-ink">{sug.name}</p>
                          {sug.isExactEan && (
                            <span className="rounded bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 text-[10px] font-bold text-emerald-900">
                              EAN identique (100%)
                            </span>
                          )}
                          {sug.shadeConflict && (
                            <span className="rounded bg-rose-100 border border-rose-300 px-1.5 py-0.5 text-[10px] font-bold text-rose-900">
                              Teinte différente
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-ink-soft">
                          <span>
                            Code-barres : <strong className="font-mono text-ink">{sug.barcode}</strong>
                          </span>
                          {sug.priceHt !== null && (
                            <span>
                              Prix HT : <strong>{formatTnd(sug.priceHt)}</strong>
                            </span>
                          )}
                          {!sug.isExactEan && (
                            <span className="rounded bg-amber-100 px-1 text-[10px] font-bold text-amber-900">
                              {sug.score}% match
                            </span>
                          )}
                        </div>
                      </div>

                      <Button
                        variant={sug.isExactEan ? 'primary' : 'soft'}
                        size="sm"
                        onClick={() =>
                          applyEanMutation.mutate({
                            productId: topfaceModalProduct.id,
                            gtin: sug.barcode,
                            offerId: sug.id,
                          })
                        }
                        disabled={applyEanMutation.isPending}
                      >
                        {sug.isExactEan ? 'Confirmer cet EAN' : 'Choisir cet EAN'}
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <Button variant="soft" onClick={() => setTopfaceModalProduct(null)}>
                  Annuler
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Assortment / Shared Barcode Conflict Resolution Modal */}
      {conflictModal &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-xs p-4 sm:p-6 overflow-y-auto animate-fade"
            onClick={() => setConflictModal(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Résolution Déclinaison / Assortiment"
              className="relative w-full max-w-2xl rounded-3xl border border-amber-300 bg-card p-6 shadow-2xl max-h-[92vh] overflow-y-auto m-auto animate-rise"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setConflictModal(null)}
                className="absolute right-4 top-4 rounded-full p-2 text-ink-ghost hover:bg-cream hover:text-ink transition-colors cursor-pointer"
                title="Fermer (Échap)"
              >
                <X className="h-5 w-5" />
              </button>

              <div>
                <div className="flex items-center gap-2.5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
                    <Boxes className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-900">
                      Déclinaison / Assortiment Fabricant
                    </span>
                    <h3 className="text-lg font-bold text-ink mt-0.5">
                      Code-barres déjà attribué à une autre variante
                    </h3>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/70 p-4 space-y-2 text-xs text-amber-950">
                  <p>
                    Le code <strong className="font-mono text-sm bg-white px-2 py-0.5 rounded border border-amber-300">{conflictModal.gtin}</strong> est déjà validé sur le catalogue pour :
                  </p>
                  <div className="rounded-xl bg-white border border-amber-200/80 p-3 shadow-2xs">
                    <div className="font-semibold text-ink text-sm">
                      {conflictModal.conflictingProduct?.name ?? 'Autre produit existant'}
                    </div>
                    <div className="text-[11px] text-ink-soft font-mono mt-0.5">
                      SKU : {conflictModal.conflictingProduct?.sku ?? `#${conflictModal.conflictingProduct?.id}`}
                    </div>
                  </div>
                  <p className="text-[11.5px] leading-relaxed text-amber-900 pt-1">
                    ℹ️ <strong>Pourquoi ce cas arrive ?</strong> Dans la parapharmacie et la puériculture (ex : biberons Nuk, shampoings Snowy Garçon/Fille, sucettes Chicco, teintes de maquillage), les fabricants assignent très souvent <strong>un seul et même code-barres GS1 officiel</strong> pour l'ensemble d'un assortiment de variantes.
                  </p>
                </div>

                <div className="mt-5 space-y-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-ink-soft">
                    Choisissez l'action appropriée pour ce produit :
                  </div>

                  {/* Option 1: Share GTIN (Official Assortment) */}
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 hover:border-emerald-300 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="rounded-md bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white uppercase">
                            Option 1 (Recommandée)
                          </span>
                          <span className="text-sm font-bold text-emerald-950">
                            Partager le code officiel (Assortiment)
                          </span>
                        </div>
                        <p className="mt-1.5 text-xs text-emerald-900 leading-relaxed">
                          Autorise les deux variantes à partager ce même code officiel <strong>{conflictModal.gtin}</strong>. Permet le rapprochement tarifaire avec Pharmashop et Parashop sur toute la gamme.
                        </p>
                      </div>
                      <Button
                        variant="primary"
                        disabled={confirmMutation.isPending}
                        onClick={() => {
                          confirmMutation.mutate({
                            productId: conflictModal.productId,
                            gtin: conflictModal.gtin,
                            allowSharedGtin: true,
                          });
                        }}
                        className="shrink-0 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-xs"
                      >
                        <CheckCheck className="h-4 w-4 mr-1" />
                        Partager ce code
                      </Button>
                    </div>
                  </div>

                  {/* Option 2: Generate Internal EAN (Prefix 2) */}
                  <div className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-4 hover:border-indigo-300 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="rounded-md bg-indigo-600 px-2 py-0.5 text-[10px] font-bold text-white uppercase">
                            Option 2 (Code Unique)
                          </span>
                          <span className="text-sm font-bold text-indigo-950">
                            Générer un code EAN-13 interne (Préfixe 2)
                          </span>
                        </div>
                        <p className="mt-1.5 text-xs text-indigo-900 leading-relaxed">
                          Génère un code EAN unique (format réservé <strong>200000...</strong> avec clé mod-10 valide). Idéal si votre système de caisse ou WooCommerce refuse absolument les doublons de code-barres.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        disabled={confirmMutation.isPending}
                        onClick={() => {
                          confirmMutation.mutate({
                            productId: conflictModal.productId,
                            generateInternal: true,
                          });
                        }}
                        className="shrink-0 border-indigo-300 bg-white hover:bg-indigo-50 text-indigo-900 font-semibold text-xs"
                      >
                        <Boxes className="h-4 w-4 mr-1 text-indigo-600" />
                        Générer code interne
                      </Button>
                    </div>
                  </div>

                  {/* Option 3: Reject / Send back to Review */}
                  <div className="flex items-center justify-between pt-2 border-t border-border mt-3">
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Rejeter le produit #${conflictModal.productId} ? Il sera renvoyé en file de révision Workbench.`,
                          )
                        ) {
                          rejectMutation.mutate({ productId: conflictModal.productId });
                          setConflictModal(null);
                        }
                      }}
                      className="text-xs font-semibold text-rose-700 hover:text-rose-800 underline cursor-pointer"
                    >
                      Ce n'est pas une variante légitime → Rejeter et renvoyer en révision
                    </button>

                    <Button variant="soft" onClick={() => setConflictModal(null)}>
                      Annuler
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
